import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ToastContainer } from 'react-toastify';
import { FaCalendarPlus, FaClock } from 'react-icons/fa';
import { useTitle } from '../../../shared/context/TitleContext';
import { apiGet, apiPost } from '../../../shared/utils/apiConfig';
import { showErrorToast, showSuccessToast } from '../../../shared/utils/toastHelper';

const dayNamesAr = {
  saturday: 'السبت',
  sunday: 'الأحد',
  monday: 'الإثنين',
  tuesday: 'الثلاثاء',
  wednesday: 'الأربعاء',
  thursday: 'الخميس',
  friday: 'الجمعة'
};

function getDayLabel(d) {
  if (!d) return '-';
  const key = (d.day || d.name || '').toLowerCase();
  return dayNamesAr[key] || d.day || d.name || `اليوم #${d.id ?? ''}`;
}

const DoctorMakeWorkDay = () => {
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const [clinics, setClinics] = useState([]);
  const [emptyDays, setEmptyDays] = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState('');
  const [selectedDayId, setSelectedDayId] = useState('');
  const [fromTime, setFromTime] = useState('09:00');
  const [toTime, setToTime] = useState('17:00');
  const [slotDuration, setSlotDuration] = useState('30');
  const [loading, setLoading] = useState(false);
  const [loadingLists, setLoadingLists] = useState(false);
  const [slotsPreview, setSlotsPreview] = useState([]);

  useEffect(() => {
    setTitle('جدول العمل - إنشاء يوم');
  }, [setTitle]);

  const loadLists = async (isMountedRef = { current: true }) => {
    try {
      setLoadingLists(true);
      const [clinicsRes, emptyDaysRes] = await Promise.all([
        apiGet('/doctor/my_clinics'),
        apiGet('/doctor_dash/empty_work_days')
      ]);
      if (!isMountedRef.current) return;
      const clinicsList = clinicsRes?.result || clinicsRes?.data || [];
      const daysList = emptyDaysRes?.result || emptyDaysRes?.data || [];
      setClinics(Array.isArray(clinicsList) ? clinicsList : []);
      setEmptyDays(Array.isArray(daysList) ? daysList : []);
      if (Array.isArray(clinicsList) && clinicsList.length > 0) {
        setSelectedClinicId(String(clinicsList[0].id ?? clinicsList[0].clinic_id ?? ''));
      }
      if (Array.isArray(daysList) && daysList.length > 0) {
        setSelectedDayId(String(daysList[0].id));
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      showErrorToast('فشل تحميل البيانات');
    } finally {
      if (isMountedRef.current) {
        setLoadingLists(false);
      }
    }
  };

  useEffect(() => {
    const mountedRef = { current: true };
    loadLists(mountedRef);
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadSlotsPreview = async (dayId, isMountedRef = { current: true }) => {
    if (!dayId) return;
    try {
      const res = await apiGet(`/doctor_dash/doctor_day_slots/${dayId}`);
      if (!isMountedRef.current) return;
      const list = res?.result || res?.data || [];
      setSlotsPreview(Array.isArray(list) ? list : []);
    } catch (err) {
      if (!isMountedRef.current) return;
      setSlotsPreview([]);
    }
  };

  useEffect(() => {
    const mountedRef = { current: true };
    loadSlotsPreview(selectedDayId, mountedRef);
    return () => {
      mountedRef.current = false;
    };
  }, [selectedDayId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedClinicId || !selectedDayId || !fromTime || !toTime || !slotDuration) {
      showErrorToast('من فضلك أكمل جميع الحقول');
      return;
    }
    try {
      setLoading(true);
      const payload = {
        id: Number(selectedDayId),
        clinic_id: Number(selectedClinicId),
        from: fromTime,
        to: toTime,
        slot_duration: String(slotDuration)
      };
      const res = await apiPost('/doctor_dash/make_work_day', payload);
      const message = res?.message || 'تم حفظ يوم العمل بنجاح';
      showSuccessToast(message);
      navigate('/doctor/clinic-working-days');
    } catch (err) {
      showErrorToast('فشل حفظ يوم العمل');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="container-fluid">
      <ToastContainer />

      <div className="d-flex justify-content-between align-items-center my-4">
        <h5 className="mb-0">إنشاء يوم عمل</h5>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card mb-4">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">العيادة</label>
                <select
                  className="form-select"
                  value={selectedClinicId}
                  onChange={(e) => setSelectedClinicId(e.target.value)}
                  disabled={loadingLists}
                >
                  <option value="" disabled>
                    {loadingLists ? 'جاري التحميل...' : 'اختر العيادة'}
                  </option>
                  {clinics.map((c) => (
                    <option key={c.id ?? c.clinic_id} value={String(c.id ?? c.clinic_id)}>
                      {c.title || c.name || c.clinic_name || `عيادة رقم ${c.id ?? c.clinic_id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">الأيام غير النشطة</label>
                <select
                  className="form-select"
                  value={selectedDayId}
                  onChange={(e) => setSelectedDayId(e.target.value)}
                  disabled={loadingLists}
                >
                  <option value="" disabled>
                    {loadingLists ? 'جاري التحميل...' : 'اختر اليوم'}
                  </option>
                  {emptyDays.map((d) => (
                    <option key={d.id} value={String(d.id)}>
                      {getDayLabel(d)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">من</label>
                <input type="time" className="form-control" value={fromTime} onChange={(e) => setFromTime(e.target.value)} />
              </div>
              <div className="col-md-2">
                <label className="form-label">إلى</label>
                <input type="time" className="form-control" value={toTime} onChange={(e) => setToTime(e.target.value)} />
              </div>
              <div className="col-md-3">
                <label className="form-label">مدة الجلسة (بالدقيقة)</label>
                <input
                  type="number"
                  className="form-control"
                  value={slotDuration}
                  onChange={(e) => setSlotDuration(e.target.value)}
                  min="1"
                  step="1"
                  placeholder="أدخل عدد الدقائق"
                />
                <small className="text-muted">مثال: 5، 10، 15، 30، 60، 120</small>
              </div>
            </div>

            <div className="mt-4 d-flex justify-content-center">
              <button
                type="submit"
                className="btn d-flex align-items-center gap-2"
                disabled={loading}
                style={{ 
                  backgroundColor: 'rgb(24, 65, 148)', 
                  color: 'white',
                  padding: '0.55rem 1.75rem',
                  fontSize: '1rem',
                  fontWeight: '600'
                }}
              >
                <FaCalendarPlus /> حفظ
              </button>
            </div>
          </form>
        </div>
      </motion.div>

      
    </div>
  );
};

export default DoctorMakeWorkDay;


